import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { Menu, Popover } from "@/components/Base/Headless";
import Pagination from "@/components/Base/Pagination";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Button from "@/components/Base/Button";

type Product = {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  barcode?: string;
  group?: string;
  type?: string;
  retailSellingPrice?: string;
  dealerSellingPrice?: string;
  productImageUrl?: string;
  status: boolean;
  isCompetitor: boolean;
  createdAt: string;
  displayOrder?: number | null;
};

function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem("productsPageSize");
    return saved ? Number(saved) : 24;
  });
  const [sortOption, setSortOption] = useState<string>("newest");
  const [productGroups, setProductGroups] = useState<Array<{ id: string; groupName: string }>>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [groupBy, setGroupBy] = useState<string>("");
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const companyId = new URLSearchParams(window.location.search).get("companyId");
        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
        const groupsUrl = companyId ? `${apiUrl}/product-groups?companyId=${companyId}` : `${apiUrl}/product-groups`;

        const res = await fetch(groupsUrl, { headers: { Authorization: `Bearer ${getToken()}` } });
        const data = await res.json().catch(() => ({}));
        setProductGroups(Array.isArray(data?.data) ? data.data : []);
      } catch (err) {
        console.error("Failed to fetch product groups:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const companyId = new URLSearchParams(window.location.search).get("companyId");
        const projectId = new URLSearchParams(window.location.search).get("projectId");

        const queryParams = new URLSearchParams();
        if (companyId) queryParams.append("companyId", companyId);
        if (projectId) queryParams.append("projectId", projectId);
        if (searchQuery) queryParams.append("q", searchQuery);
        queryParams.append("page", currentPage.toString());
        queryParams.append("pageSize", pageSize.toString());

        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
        const res = await fetch(`${apiUrl}/products?${queryParams.toString()}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json().catch(() => ({}));
        setProducts(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentPage, searchQuery, pageSize]);

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this product?")) return;

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const res = await fetch(`${apiUrl}/products/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (res.ok) {
        setProducts((prev) => prev.filter((p) => p.id !== id));
      } else {
        const error = await res.json().catch(() => ({}));
        alert(`Failed to delete product: ${error?.error || "Unknown error"}`);
      }
    } catch (error: unknown) {
      console.error("Delete failed:", error);
      alert("Something went wrong deleting product");
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const handleDragStart = (e: React.DragEvent, productId: string) => {
    setDraggedProductId(productId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", productId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e: React.DragEvent, targetProductId: string, targetGroup?: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedProductId || draggedProductId === targetProductId) {
      setDraggedProductId(null);
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

    if (groupedProducts && targetGroup) {
      let draggedGroup: string | null = null;
      for (const [groupName, groupProducts] of groupedProducts) {
        if (groupProducts.some((p) => p.id === draggedProductId)) {
          draggedGroup = groupName;
          break;
        }
      }
      if (draggedGroup !== targetGroup) {
        setDraggedProductId(null);
        return;
      }

      const groupProducts = groupedProducts.find(([name]) => name === targetGroup)?.[1] || [];
      const draggedIndex = groupProducts.findIndex((p) => p.id === draggedProductId);
      const targetIndex = groupProducts.findIndex((p) => p.id === targetProductId);
      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedProductId(null);
        return;
      }

      const reorderedGroupProducts = [...groupProducts];
      const [draggedProduct] = reorderedGroupProducts.splice(draggedIndex, 1);
      reorderedGroupProducts.splice(targetIndex, 0, draggedProduct);

      const allProducts: Product[] = [];
      for (const [groupName, groupProds] of groupedProducts) {
        if (groupName === targetGroup) allProducts.push(...reorderedGroupProducts);
        else allProducts.push(...groupProds);
      }

      setIsUpdatingOrder(true);
      try {
        const productOrders = allProducts.map((product, index) => ({ id: product.id, displayOrder: index + 1 }));
        const res = await fetch(`${apiUrl}/products/display-order`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ productOrders }),
        });
        if (res.ok) {
          const companyId = new URLSearchParams(window.location.search).get("companyId");
          const projectId = new URLSearchParams(window.location.search).get("projectId");
          const queryParams = new URLSearchParams();
          if (companyId) queryParams.append("companyId", companyId);
          if (projectId) queryParams.append("projectId", projectId);
          if (searchQuery) queryParams.append("q", searchQuery);
          queryParams.append("page", currentPage.toString());
          queryParams.append("pageSize", pageSize.toString());

          const productsRes = await fetch(`${apiUrl}/products?${queryParams.toString()}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const productsData = await productsRes.json().catch(() => ({}));
          setProducts(Array.isArray(productsData?.data) ? productsData.data : []);
        } else {
          alert("Failed to update product order");
        }
      } catch (error) {
        console.error("Failed to update product order:", error);
        alert("Failed to update product order");
      } finally {
        setIsUpdatingOrder(false);
        setDraggedProductId(null);
      }
    } else {
      const currentProducts = filteredAndSortedProducts;
      const draggedIndex = currentProducts.findIndex((p) => p.id === draggedProductId);
      const targetIndex = currentProducts.findIndex((p) => p.id === targetProductId);
      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedProductId(null);
        return;
      }

      const reorderedProducts = [...currentProducts];
      const [draggedProduct] = reorderedProducts.splice(draggedIndex, 1);
      reorderedProducts.splice(targetIndex, 0, draggedProduct);

      setIsUpdatingOrder(true);
      try {
        const productOrders = reorderedProducts.map((product, index) => ({ id: product.id, displayOrder: index + 1 }));
        const res = await fetch(`${apiUrl}/products/display-order`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ productOrders }),
        });
        if (res.ok) {
          const companyId = new URLSearchParams(window.location.search).get("companyId");
          const projectId = new URLSearchParams(window.location.search).get("projectId");
          const queryParams = new URLSearchParams();
          if (companyId) queryParams.append("companyId", companyId);
          if (projectId) queryParams.append("projectId", projectId);
          if (searchQuery) queryParams.append("q", searchQuery);
          queryParams.append("page", currentPage.toString());
          queryParams.append("pageSize", pageSize.toString());

          const productsRes = await fetch(`${apiUrl}/products?${queryParams.toString()}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const productsData = await productsRes.json().catch(() => ({}));
          setProducts(Array.isArray(productsData?.data) ? productsData.data : []);
        } else {
          const errorData = await res.json().catch(() => ({}));
          alert(`Failed to update product order: ${errorData?.message || "Unknown error"}`);
        }
      } catch (error) {
        console.error("Failed to update product order:", error);
        alert("Failed to update product order");
      } finally {
        setIsUpdatingOrder(false);
        setDraggedProductId(null);
      }
    }
  };

  const renderProductCard = (product: Product, _index?: number, groupName?: string) => (
    <div
      key={product.id}
      draggable={!isUpdatingOrder}
      onDragStart={(e) => handleDragStart(e, product.id)}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleDragOver(e);
      }}
      onDrop={(e) => handleDrop(e, product.id, groupName)}
      className={`col-span-12 sm:col-span-6 xl:col-span-3 border-dashed border-slate-300/80 [&:nth-child(4n)]:border-r-0 px-5 py-5 [&:nth-last-child(-n+4)]:border-b-0 border-r border-b flex flex-col relative ${
        draggedProductId === product.id ? "opacity-50" : ""
      } ${!isUpdatingOrder ? "cursor-move" : ""}`}
    >
      {!isUpdatingOrder && (
        <div className="absolute top-2 left-2 z-20 p-1 bg-white/80 dark:bg-darkmode-600/80 rounded cursor-move">
          <Lucide icon="GripVertical" className="w-4 h-4 text-slate-500" />
        </div>
      )}
      <div className="relative overflow-hidden rounded-lg h-56 flex items-center justify-center before:block before:absolute before:w-full before:h-full before:top-0 before:left-0 before:z-10 before:bg-gradient-to-t before:from-slate-900/90 before:to-black/20">
        <img alt={product.name} className="rounded-md max-h-full w-auto" src={product.productImageUrl || "/api/placeholder/400/300"} />
        {product.status ? (
          <span className="absolute top-0 right-0 z-10 px-2.5 py-1 m-5 text-xs text-white rounded-lg bg-success/80 font-medium border-white/20 border">
            Active
          </span>
        ) : (
          <span className="absolute top-0 right-0 z-10 px-2.5 py-1 m-5 text-xs text-white rounded-lg bg-pending/80 font-medium border-white/20 border">
            Inactive
          </span>
        )}
        {product.isCompetitor && (
          <span className="absolute top-0 right-0 z-10 px-2.5 py-1 m-5 text-xs text-white rounded-lg bg-danger/80 font-medium border-white/20 border">
            Competitor
          </span>
        )}
        <div className="absolute bottom-0 z-10 w-full px-5 pb-6 text-white">
          <a href="#" className="block text-lg font-medium truncate">
            {product.name}
          </a>
          <span className="mt-3 text-xs text-white/80">{product.group || product.type || "General"}</span>
        </div>
      </div>
      <div className="pt-5">
        <div className="flex flex-col gap-3.5 mb-5 pb-5 mt-auto border-b border-dashed border-slate-300/70">
          <div className="flex items-center">
            <div className="text-slate-500">SKU:</div>
            <div className="ml-auto">
              <div className="text-xs font-medium">{product.sku || "—"}</div>
            </div>
          </div>
          <div className="flex items-center">
            <div className="text-slate-500">Retail Price:</div>
            <div className="ml-auto">
              <div className="text-xs font-medium">
                {product.retailSellingPrice ? `RM ${parseFloat(product.retailSellingPrice).toFixed(2)}` : "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <div className="text-slate-500">Dealer Price:</div>
            <div className="ml-auto">
              <div className="text-xs font-medium">
                {product.dealerSellingPrice ? `RM ${parseFloat(product.dealerSellingPrice).toFixed(2)}` : "—"}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <button type="button" className="flex items-center mr-auto text-primary" aria-label={`Preview ${product.name}`}>
            <Lucide icon="KanbanSquare" className="w-4 h-4 stroke-[1.3] mr-1.5" />
            Preview
          </button>
          <Link
            to={(() => {
              const searchParams = new URLSearchParams(window.location.search);
              const projectId = searchParams.get("projectId");
              const companyId = searchParams.get("companyId");

              let editUrl = `/products/${product.id}/edit`;
              if (projectId) editUrl += `?projectId=${projectId}`;
              else if (companyId) editUrl += `?companyId=${companyId}`;
              return editUrl;
            })()}
            className="flex items-center mr-3"
          >
            <Lucide icon="CheckSquare" className="w-4 h-4 stroke-[1.3] mr-1.5" /> Edit
          </Link>
          <button type="button" onClick={() => handleDelete(product.id)} className="flex items-center text-danger" aria-label={`Delete ${product.name}`}>
            <Lucide icon="Trash2" className="w-4 h-4 stroke-[1.3] mr-1.5" /> Delete
          </button>
        </div>
      </div>
    </div>
  );

  const filteredAndSortedProducts = [...products]
    .filter((product) => {
      if (selectedGroup && product.group !== selectedGroup) return false;
      return true;
    })
    .sort((a, b) => {
      const aHasOrder = a.displayOrder !== null && a.displayOrder !== undefined;
      const bHasOrder = b.displayOrder !== null && b.displayOrder !== undefined;

      if (aHasOrder && bHasOrder) return a.displayOrder! - b.displayOrder!;
      if (aHasOrder && !bHasOrder) return -1;
      if (!aHasOrder && bHasOrder) return 1;

      switch (sortOption) {
        case "newest":
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case "oldest":
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "price_asc":
          return parseFloat(a.retailSellingPrice || "0") - parseFloat(b.retailSellingPrice || "0");
        case "price_desc":
          return parseFloat(b.retailSellingPrice || "0") - parseFloat(a.retailSellingPrice || "0");
        case "active_first":
          return b.status === a.status ? 0 : b.status ? 1 : -1;
        case "inactive_first":
          return b.status === a.status ? 0 : a.status ? 1 : -1;
        default:
          return 0;
      }
    });

  const groupedProducts = groupBy
    ? (() => {
        const groups = new Map<string, Product[]>();
        filteredAndSortedProducts.forEach((product) => {
          let groupKey = "";
          switch (groupBy) {
            case "group":
              groupKey = product.group || "No Group";
              break;
            case "type":
              groupKey = product.type || "No Type";
              break;
            case "status":
              groupKey = product.status ? "Active" : "Inactive";
              break;
            default:
              groupKey = "All";
          }
          if (!groups.has(groupKey)) groups.set(groupKey, []);
          groups.get(groupKey)!.push(product);
        });
        return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
      })()
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
          <div>Loading products...</div>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
          <div className="col-span-12">
            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Products</div>
              <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                <Button
                  variant="primary"
                  className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                  onClick={() => {
                    const searchParams = new URLSearchParams(window.location.search);
                    const companyId = searchParams.get("companyId");
                    const projectId = searchParams.get("projectId");

                    let addUrl = "/products/add";
                    if (projectId) addUrl += `?projectId=${projectId}`;
                    else if (companyId) addUrl += `?companyId=${companyId}`;

                    navigate(addUrl);
                  }}
                >
                  <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" /> Add New Product
                </Button>

                <Button
                  variant="outline-secondary"
                  className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                  onClick={() => {
                    const searchParams = new URLSearchParams(window.location.search);
                    const companyId = searchParams.get("companyId");
                    const projectId = searchParams.get("projectId");

                    let groupUrl = "/product-groups";
                    if (projectId) groupUrl += `?projectId=${projectId}`;
                    else if (companyId) groupUrl += `?companyId=${companyId}`;

                    navigate(groupUrl);
                  }}
                >
                  <Lucide icon="Package" className="stroke-[1.3] w-4 h-4 mr-2" /> Product Group
                </Button>

                <Button
                  variant="outline-secondary"
                  className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                  onClick={() => {
                    const searchParams = new URLSearchParams(window.location.search);
                    const companyId = searchParams.get("companyId");
                    const projectId = searchParams.get("projectId");

                    let typeUrl = "/product-types";
                    if (projectId) typeUrl += `?projectId=${projectId}`;
                    else if (companyId) typeUrl += `?companyId=${companyId}`;

                    navigate(typeUrl);
                  }}
                >
                  <Lucide icon="Tag" className="stroke-[1.3] w-4 h-4 mr-2" /> Product Type
                </Button>

                <Button
                  variant="outline-secondary"
                  className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                  onClick={() => {
                    const searchParams = new URLSearchParams(window.location.search);
                    const companyId = searchParams.get("companyId");
                    const projectId = searchParams.get("projectId");

                    let packUrl = "/pack-types";
                    if (projectId) packUrl += `?projectId=${projectId}`;
                    else if (companyId) packUrl += `?companyId=${companyId}`;

                    navigate(packUrl);
                  }}
                >
                  <Lucide icon="Box" className="stroke-[1.3] w-4 h-4 mr-2" /> Pack Type
                </Button>
              </div>
            </div>

            <div className="mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                  <div>
                    <form onSubmit={handleSearch}>
                      <div className="relative">
                        <Lucide icon="Search" className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500" />
                        <FormInput
                          type="text"
                          placeholder="Search products..."
                          className="pl-9 sm:w-64 rounded-[0.5rem]"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                        />
                      </div>
                    </form>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 sm:ml-auto">
                    <Menu>
                      <Menu.Button as={Button} variant="outline-secondary" className="w-full sm:w-auto">
                        <Lucide icon="Download" className="stroke-[1.3] w-4 h-4 mr-2" />
                        Export
                        <Lucide icon="ChevronDown" className="stroke-[1.3] w-4 h-4 ml-2" />
                      </Menu.Button>
                      <Menu.Items className="w-40">
                        <Menu.Item>
                          <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" /> PDF
                        </Menu.Item>
                        <Menu.Item>
                          <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" /> CSV
                        </Menu.Item>
                      </Menu.Items>
                    </Menu>
                    <Popover className="inline-block">
                      {({ close }) => (
                        <>
                          <Popover.Button as={Button} variant="outline-secondary" className="w-full sm:w-auto">
                            <Lucide icon="ArrowDownWideNarrow" className="stroke-[1.3] w-4 h-4 mr-2" />
                            Filter
                            {(selectedGroup || groupBy) && (
                              <div className="flex items-center justify-center h-5 px-1.5 ml-2 text-xs font-medium border rounded-full bg-slate-100 dark:bg-darkmode-400">
                                {(selectedGroup ? 1 : 0) + (groupBy ? 1 : 0)}
                              </div>
                            )}
                          </Popover.Button>
                          <Popover.Panel placement="bottom-end">
                            <div className="p-2">
                              <div>
                                <div className="text-left text-slate-500">Product Group</div>
                                <FormSelect className="flex-1 mt-2" value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}>
                                  <option value="">All Groups</option>
                                  {productGroups.map((group) => (
                                    <option key={group.id} value={group.groupName}>
                                      {group.groupName}
                                    </option>
                                  ))}
                                </FormSelect>
                              </div>
                              <div className="mt-3">
                                <div className="text-left text-slate-500">Group By</div>
                                <FormSelect className="flex-1 mt-2" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                                  <option value="">None</option>
                                  <option value="group">Product Group</option>
                                  <option value="type">Product Type</option>
                                  <option value="status">Status</option>
                                </FormSelect>
                              </div>
                              <div className="flex items-center mt-4">
                                <Button
                                  variant="secondary"
                                  onClick={() => {
                                    setSelectedGroup("");
                                    setGroupBy("");
                                    close();
                                  }}
                                  className="w-32 ml-auto"
                                >
                                  Clear
                                </Button>
                                <Button variant="primary" className="w-32 ml-2" onClick={() => close()}>
                                  Apply
                                </Button>
                              </div>
                            </div>
                          </Popover.Panel>
                        </>
                      )}
                    </Popover>
                  </div>
                </div>
                <div className="overflow-hidden">
                  {groupedProducts ? (
                    groupedProducts.map(([groupName, groupProducts]) => (
                      <div key={groupName} className="mb-8">
                        <div className="px-5 mb-4">
                          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                            {groupName} ({groupProducts.length})
                          </h3>
                        </div>
                        <div className="grid grid-cols-12 px-5 -mx-5 border-dashed border-y">
                          {groupProducts.map((product, idx) => renderProductCard(product, idx, groupName))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="grid grid-cols-12 px-5 -mx-5 border-dashed border-y">
                      {filteredAndSortedProducts.map((product, idx) => renderProductCard(product, idx, undefined))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                  <div className="text-sm text-slate-500 mr-4">
                    {total > 0 ? (
                      <>
                        Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, total)} of {total} products
                      </>
                    ) : (
                      "No products found"
                    )}
                  </div>
                  <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                    {currentPage > 1 && (
                      <Pagination.Link onClick={() => setCurrentPage(1)}>
                        <Lucide icon="ChevronsLeft" className="w-4 h-4" />
                      </Pagination.Link>
                    )}
                    {currentPage > 1 && (
                      <Pagination.Link onClick={() => setCurrentPage(currentPage - 1)}>
                        <Lucide icon="ChevronLeft" className="w-4 h-4" />
                      </Pagination.Link>
                    )}
                    {totalPages > 7 && currentPage > 4 && (
                      <>
                        <Pagination.Link onClick={() => setCurrentPage(1)}>1</Pagination.Link>
                        {currentPage > 5 && <Pagination.Link>...</Pagination.Link>}
                      </>
                    )}
                    {Array.from({ length: totalPages }).map((_, i) => {
                      const pageNum = i + 1;
                      if (
                        totalPages <= 7 ||
                        pageNum <= 3 ||
                        pageNum > totalPages - 3 ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <Pagination.Link key={i} active={currentPage === pageNum} onClick={() => setCurrentPage(pageNum)}>
                            {pageNum}
                          </Pagination.Link>
                        );
                      }
                      return null;
                    })}
                    {totalPages > 7 && currentPage < totalPages - 3 && (
                      <>
                        {currentPage < totalPages - 4 && <Pagination.Link>...</Pagination.Link>}
                        <Pagination.Link onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Link>
                      </>
                    )}
                    {currentPage < totalPages && (
                      <Pagination.Link onClick={() => setCurrentPage(currentPage + 1)}>
                        <Lucide icon="ChevronRight" className="w-4 h-4" />
                      </Pagination.Link>
                    )}
                    {currentPage < totalPages && (
                      <Pagination.Link onClick={() => setCurrentPage(totalPages)}>
                        <Lucide icon="ChevronsRight" className="w-4 h-4" />
                      </Pagination.Link>
                    )}
                  </Pagination>
                  <FormSelect
                    className="sm:w-20 rounded-[0.5rem]"
                    value={pageSize}
                    onChange={(e) => {
                      const newPageSize = Number(e.target.value);
                      setPageSize(newPageSize);
                      setCurrentPage(1);
                      localStorage.setItem("productsPageSize", newPageSize.toString());
                    }}
                  >
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                    <option value={48}>48</option>
                    <option value={96}>96</option>
                  </FormSelect>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}

export default ProductsPage;

